// ============================================================
// API client. Single-origin: paths are same-origin in production
// (FastAPI serves this bundle) and proxied in dev by vite.config.js.
//
// ASSUMED BACKEND CONTRACT (align with your slice 1-3 serializers):
//   POST /auth/login   body {email, password}   -> {access_token, token_type}
//   GET  /me                                     -> {id, email, ...}
//   GET  /vehicles                               -> [Vehicle]
//   GET  /vehicles/:id                           -> Vehicle
//
//   Vehicle = {
//     id, vin, display_name, model, image_url,
//     state,                       // "online" | "asleep" | "offline"
//     last_snapshot: {             // the slice-3 snapshot shape
//       battery_level, odometer, speed, locked,
//       inside_temp, outside_temp,
//       tpms_fl, tpms_fr, tpms_rl, tpms_rr,
//       doors: { df, pf, dr, pr, ft, rt },   // driver/passenger front/rear, frunk, trunk
//       window_fd, window_fp, window_rd, window_rp,
//       location: { lat, lng },
//       sentry_mode, charge_state, updated_at
//     }
//   }
//
// If any names differ, this file is the one place to remap them.
// ============================================================

// Same-origin on the web (FastAPI serves this bundle). Inside the Capacitor
// native shell the app is served from capacitor://localhost, so API calls must
// target the production origin absolutely.
const IS_NATIVE =
  typeof window !== "undefined" &&
  (window.Capacitor?.isNativePlatform?.() || window.location.protocol === "capacitor:");
const API_BASE = IS_NATIVE ? "https://www.myteslaapp.net" : "";
const TOKEN_KEY = "tm3_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

async function request(path, { method = "GET", body, auth = true } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const t = getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  }
  let res;
  try {
    res = await fetch(API_BASE + path, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(0, "Can't reach the server. Check your connection and try again.");
  }

  if (res.status === 401 && auth) {
    setToken(null);
    // let callers/route guards react to the missing token
    throw new ApiError(401, "Your session expired. Sign in again.");
  }

  if (!res.ok) {
    let msg = `Request failed (${res.status}).`;
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") {
        msg = data.detail;
      } else if (Array.isArray(data?.detail) && data.detail.length) {
        // FastAPI validation errors come as a list of {loc, msg, ...}
        const first = data.detail[0];
        const loc = Array.isArray(first?.loc) ? first.loc.join(".") : "";
        msg = `${first?.msg || "Invalid request"}${loc ? ` (${loc})` : ""}`;
      }
    } catch (e) {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, msg);
  }

  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  async login(email, password, mfaCode) {
    const body = { email, password };
    if (mfaCode) body.mfa_code = mfaCode;
    const data = await request("/auth/login", { method: "POST", body, auth: false });
    const token = data.access_token || data.token;
    if (!token) throw new ApiError(500, "Login succeeded but no token was returned.");
    setToken(token);
    return token;
  },

  async register(body) {
    const data = await request("/auth/register", { method: "POST", body, auth: false });
    const token = data.access_token || data.token;
    if (token) setToken(token);
    return data;
  },

  registrationStatus() {
    return request("/auth/registration", { auth: false });
  },
  pricing() {
    return request("/auth/pricing", { auth: false });
  },
  marketingPricing() {
    return request("/auth/marketing-pricing", { auth: false });
  },
  adminPublishPricing() {
    return request("/admin/pricing/publish", { method: "POST" });
  },
  adminPublishStatus() {
    return request("/admin/pricing/publish");
  },
  // --- automated checkout (PayPal) ---
  checkoutQuote(body) {
    return request("/billing/quote", { method: "POST", body });
  },
  checkoutStart(body) {
    return request("/billing/checkout", { method: "POST", body });
  },
  checkoutCapture(orderId) {
    return request("/billing/capture", { method: "POST", body: { order_id: orderId } });
  },

  // --- QR sign-in (phone approves desktop) ---
  qrStart() {
    return request("/auth/qr/start", { method: "POST", auth: false });
  },
  qrInfo(token) {
    return request(`/auth/qr/info?token=${encodeURIComponent(token)}`, { auth: false });
  },
  qrStatus(token) {
    return request(`/auth/qr/status?token=${encodeURIComponent(token)}`, { auth: false });
  },
  qrApprove(token) {
    return request("/auth/qr/approve", { method: "POST", body: { token } });
  },
  qrDeny(token) {
    return request("/auth/qr/deny", { method: "POST", body: { token } });
  },

  async demoLogin() {
    const data = await request("/auth/demo-login", { method: "POST", auth: false });
    const token = data.access_token || data.token;
    if (token) setToken(token);
    return data;
  },
  logout() {
    setToken(null);
  },

  me() {
    return request("/auth/me");
  },

  // --- MFA (TOTP) enrollment ---
  mfaSetup() {
    return request("/auth/mfa/setup", { method: "POST" });
  },
  mfaActivate(body) {
    return request("/auth/mfa/activate", { method: "POST", body });
  },
  mfaDisable(body) {
    return request("/auth/mfa/disable", { method: "POST", body });
  },

  vehicles() {
    return request("/vehicles");
  },
  telemetryWake(id) {
    return request(`/vehicles/${id}/telemetry/wake`, { method: "POST" });
  },
  telemetryConfigure(id) {
    return request(`/vehicles/${id}/telemetry/configure`, { method: "POST" });
  },
  telemetryStatus(id) {
    return request(`/vehicles/${id}/telemetry/status`);
  },

  // Fast-changing snapshot fields for the driving view (polled on a short
  // interval). Cheap DB read; safe to call every couple of seconds.
  live(id) {
    return request(`/vehicles/${id}/live`);
  },

  vehicle(id) {
    return request(`/vehicles/${id}`);
  },

  // Owner edits to a vehicle (currently: trim_override). PATCH returns the
  // resolved pack so the UI can reflect it immediately.
  updateVehicle(id, body) {
    return request(`/vehicles/${id}`, { method: "PATCH", body });
  },

  // --- Metric Engine (Phase 1): battery-health series + daily/weekly efficiency.
  // Feeds the "Today" insight strip. days defaults to 30 server-side.
  metrics(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return request(`/vehicles/${id}/metrics${suffix}`);
  },

  // --- Insight Engine (Phase 3): deterministic "what changed" feed.
  insights(id) {
    return request(`/vehicles/${id}/insights`);
  },

  // --- Weekly Report Card (Phase 4): grounded AI narration of the insights.
  reportWeekly(id) {
    return request(`/vehicles/${id}/report/weekly`);
  },

  // --- Contextual recommendations (Phase 7 / A1): request-time, context-aware
  // actions from live charge %, location, and weather. Refreshes through the day.
  recommendations(id) {
    return request(`/vehicles/${id}/recommendations`);
  },

  // --- Drive recaps (#9): captured drives + their scored recaps. drives() feeds
  // the Driving-tab history; driveRecapPending() feeds the insights recap card;
  // markDriveViewed() clears it from the feed (the drive stays stored).
  drives(id, params = {}) {
    const values = typeof params === "number" ? { limit: params } : params;
    const qs = new URLSearchParams();
    Object.entries(values).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    return request(`/vehicles/${id}/drives${qs.size ? `?${qs}` : ""}`);
  },
  drive(id, driveId) {
    return request(`/vehicles/${id}/drives/${driveId}`);
  },
  driveRecapPending(id) {
    return request(`/vehicles/${id}/drives/recap/pending`);
  },
  markDriveViewed(id, driveId) {
    return request(`/vehicles/${id}/drives/${driveId}/viewed`, { method: "POST" });
  },
  // Persisted 0-60 launches across drives + personal best (performance_mode).
  launches(id, limit = 20) {
    return request(`/vehicles/${id}/launches?limit=${limit}`);
  },

  // --- Maintenance log + reminders (vehicle health) ---
  maintenanceCatalog(id) {
    return request(`/vehicles/${id}/maintenance/catalog`);
  },
  maintenance(id) {
    return request(`/vehicles/${id}/maintenance`);
  },
  createMaintenance(id, body) {
    return request(`/vehicles/${id}/maintenance`, { method: "POST", body });
  },
  updateMaintenance(id, rid, body) {
    return request(`/vehicles/${id}/maintenance/${rid}`, { method: "PATCH", body });
  },
  deleteMaintenance(id, rid) {
    return request(`/vehicles/${id}/maintenance/${rid}`, { method: "DELETE" });
  },
  // Wear-estimate override for one item. Empty body clears it (back to catalog).
  setWearOverride(id, body) {
    return request(`/vehicles/${id}/maintenance/wear-override`, { method: "PUT", body });
  },
  maintenanceReminders(id) {
    return request(`/vehicles/${id}/maintenance/reminders`);
  },

  // Predictive health: efficiency-drop attribution (cold weather + tire
  // pressure + remainder) over a recent window vs a baseline window.
  healthEfficiency(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return request(`/vehicles/${id}/health/efficiency${suffix}`);
  },
  // Vehicle Health Score (vitals landing): 0-100 index from maintenance status.
  healthScore(id) {
    return request(`/vehicles/${id}/health/score`);
  },
  dismissMaintenanceReminder(id, rid) {
    return request(`/vehicles/${id}/maintenance/${rid}/dismiss`, { method: "POST" });
  },
  // Receipt file upload/serve (auth-gated). Upload is multipart; view fetches the
  // private file with auth and returns an object URL for the caller to open/revoke.
  async uploadReceipt(id, rid, file) {
    const fd = new FormData();
    fd.append("file", file);
    const t = getToken();
    const res = await fetch(`${API_BASE}/vehicles/${id}/maintenance/${rid}/receipt`, {
      method: "POST",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
    });
    if (!res.ok) {
      let msg = `Upload failed (${res.status}).`;
      try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch (e) { /* ignore */ }
      throw new ApiError(res.status, msg);
    }
    return res.json();
  },
  deleteReceipt(id, rid) {
    return request(`/vehicles/${id}/maintenance/${rid}/receipt`, { method: "DELETE" });
  },
  async fetchReceiptObjectUrl(id, rid) {
    const t = getToken();
    const res = await fetch(`${API_BASE}/vehicles/${id}/maintenance/${rid}/receipt`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, `Couldn't load receipt (${res.status}).`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  // --- Automations (if-this-then-that rules) ---
  automationsCatalog(id) {
    return request(`/vehicles/${id}/automations/catalog`);
  },
  automations(id) {
    return request(`/vehicles/${id}/automations`);
  },
  automation(id, aid) {
    return request(`/vehicles/${id}/automations/${aid}`);
  },
  createAutomation(id, body) {
    return request(`/vehicles/${id}/automations`, { method: "POST", body });
  },
  updateAutomation(id, aid, body) {
    return request(`/vehicles/${id}/automations/${aid}`, { method: "PATCH", body });
  },
  deleteAutomation(id, aid) {
    return request(`/vehicles/${id}/automations/${aid}`, { method: "DELETE" });
  },
  runAutomation(id, aid) {
    return request(`/vehicles/${id}/automations/${aid}/run`, { method: "POST" });
  },
  automationRuns(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return request(`/vehicles/${id}/automations/runs${suffix}`);
  },

  adminErrors() {
    return request("/admin/errors");
  },
  adminVitals(days = 7) {
    return request(`/admin/vitals?days=${days}`);
  },
  adminClearErrors() {
    return request("/admin/errors", { method: "DELETE" });
  },
  adminTestError() {
    return request("/admin/errors/test", { method: "POST" });
  },
  // --- Admin (owner/admin only) ---
  adminMeta() {
    return request("/admin/meta");
  },
  adminUsers(q) {
    return request(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  },
  adminCreateUser(body) {
    return request("/admin/users", { method: "POST", body });
  },
  adminUpdateUser(uid, body) {
    return request(`/admin/users/${uid}`, { method: "PATCH", body });
  },
  adminResetPassword(uid, body = {}) {
    return request(`/admin/users/${uid}/password`, { method: "POST", body });
  },
  async impersonate(uid) {
    const data = await request(`/admin/users/${uid}/impersonate`, { method: "POST" });
    const token = data.access_token || data.token;
    if (token) setToken(token);
    return data;
  },
  async stopImpersonation() {
    const data = await request("/auth/impersonate/stop", { method: "POST" });
    const token = data.access_token || data.token;
    if (token) setToken(token);
    return data;
  },
  adminDeleteUser(uid) {
    return request(`/admin/users/${uid}`, { method: "DELETE" });
  },
  adminSettings() {
    return request("/admin/settings");
  },
  adminUpdateSettings(body) {
    return request("/admin/settings", { method: "PUT", body });
  },
  adminLicenses() {
    return request("/admin/licenses");
  },
  adminCreateLicense(body) {
    return request("/admin/licenses", { method: "POST", body });
  },
  adminUpdateLicense(id, body) {
    return request(`/admin/licenses/${id}`, { method: "PATCH", body });
  },
  adminDeleteLicense(id) {
    return request(`/admin/licenses/${id}`, { method: "DELETE" });
  },
  // Billing / pricing / discounts (perm: billing)
  adminPricing() {
    return request("/admin/pricing");
  },
  adminUpdatePricing(prices, mode) {
    return request("/admin/pricing", { method: "PUT", body: { prices, mode } });
  },
  adminBilling() {
    return request("/admin/billing");
  },
  adminUpdateBilling(body) {
    return request("/admin/billing", { method: "PUT", body });
  },
  adminRenewals() {
    return request("/admin/billing/renewals");
  },
  adminRunBillingCycle() {
    return request("/admin/billing/run-cycle", { method: "POST" });
  },
  adminZones() {
    return request("/admin/pricing/zones");
  },
  adminUpdateZones(zones) {
    return request("/admin/pricing/zones", { method: "PUT", body: { zones } });
  },
  adminNotifications() {
    return request("/admin/notifications");
  },
  adminUpdateNotifications(body) {
    return request("/admin/notifications", { method: "PUT", body });
  },
  adminTestNotification() {
    return request("/admin/notifications/test", { method: "POST" });
  },
  adminMetrics(range = "30d") {
    return request(`/admin/metrics?range=${encodeURIComponent(range)}`);
  },
  pushConfig() {
    return request("/auth/push-config");
  },
  adminDiscounts() {
    return request("/admin/discounts");
  },
  adminCreateDiscount(body) {
    return request("/admin/discounts", { method: "POST", body });
  },
  adminPatchDiscount(id, body) {
    return request(`/admin/discounts/${id}`, { method: "PATCH", body });
  },
  adminDeleteDiscount(id) {
    return request(`/admin/discounts/${id}`, { method: "DELETE" });
  },
  adminAudit() {
    return request("/admin/audit");
  },
  async downloadBackup() {
    const t = getToken();
    const res = await fetch(`${API_BASE}/admin/backup`, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
    if (!res.ok) {
      let msg = `Backup failed (${res.status}).`;
      try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch (e) { /* ignore */ }
      throw new ApiError(res.status, msg);
    }
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    return { blob, name: m ? m[1] : "teslam3-backup.dump" };
  },
  async restoreBackup(file, confirm) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("confirm", confirm);
    const t = getToken();
    const res = await fetch(`${API_BASE}/admin/restore`, {
      method: "POST",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
    });
    if (!res.ok) {
      let msg = `Restore failed (${res.status}).`;
      try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch (e) { /* ignore */ }
      throw new ApiError(res.status, msg);
    }
    return res.json();
  },
  redeemLicense(key) {
    return request("/auth/redeem", { method: "POST", body: { license_key: key } });
  },
  linkStart() {
    return request("/vehicles/link/start", { method: "POST" });
  },

  // --- Feedback ---
  submitFeedback(body) {
    return request("/feedback", { method: "POST", body });
  },
  myFeedback() {
    return request("/feedback/mine");
  },
  feedbackBoard(status) {
    return request(`/feedback${status ? `?status=${encodeURIComponent(status)}` : ""}`);
  },
  feedbackThread(id) {
    return request(`/feedback/${id}`);
  },
  addFeedbackComment(id, text) {
    return request(`/feedback/${id}/comments`, { method: "POST", body: { body: text } });
  },
  setFeedbackStatus(id, status) {
    return request(`/feedback/${id}`, { method: "PATCH", body: { status } });
  },
  deleteFeedback(id) {
    return request(`/feedback/${id}`, { method: "DELETE" });
  },
  async uploadFeedbackScreenshot(id, file) {
    const fd = new FormData();
    fd.append("file", file);
    const t = getToken();
    const res = await fetch(`${API_BASE}/feedback/${id}/attachments`, {
      method: "POST",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
    });
    if (!res.ok) {
      let msg = `Upload failed (${res.status}).`;
      try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch (e) { /* ignore */ }
      throw new ApiError(res.status, msg);
    }
    return res.json();
  },
  async feedbackAttachmentUrl(id, aid) {
    const t = getToken();
    const res = await fetch(`${API_BASE}/feedback/${id}/attachments/${aid}`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  },

  // --- Trip Builder (#8, B1): saved route plans + on-demand route geometry.
  tripPlans(id) {
    return request(`/vehicles/${id}/trip-plans`);
  },
  tripPlan(id, planId) {
    return request(`/vehicles/${id}/trip-plans/${planId}`);
  },
  createTripPlan(id, body) {
    return request(`/vehicles/${id}/trip-plans`, { method: "POST", body });
  },
  updateTripPlan(id, planId, body) {
    return request(`/vehicles/${id}/trip-plans/${planId}`, { method: "PUT", body });
  },
  deleteTripPlan(id, planId) {
    return request(`/vehicles/${id}/trip-plans/${planId}`, { method: "DELETE" });
  },
  planRoute(id, stops) {
    return request(`/vehicles/${id}/trip-plans/route`, { method: "POST", body: { stops } });
  },
  tripPlanChargers(id, points, source = "superchargers", radius_km = 5) {
    return request(`/vehicles/${id}/trip-plans/chargers`, {
      method: "POST",
      body: { points, source, radius_km },
    });
  },
  sendTripToCar(id, stops) {
    return request(`/vehicles/${id}/trip-plans/send`, { method: "POST", body: { stops } });
  },

  // Send a single destination (a charger) to the car navigation.
  navigateToPoint(id, lat, lng, label) {
    return request(`/vehicles/${id}/trip-plans/navigate`, { method: "POST", body: { lat, lng, label } });
  },
  // Partial update (pin/unpin to landing, or rename) without resending stops.
  patchTripPlan(id, planId, body) {
    return request(`/vehicles/${id}/trip-plans/${planId}`, { method: "PATCH", body });
  },
  // --- Security tab (slice 5) ---
  sentryEvents(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return request(`/vehicles/${id}/sentry-events${suffix}`);
  },

  // Unified, searchable security log across all categories (Foundation).
  securityEvents(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return request(`/vehicles/${id}/security-events${suffix}`);
  },

  securityState(id) {
    return request(`/vehicles/${id}/security-state`);
  },

  setSentry(id, on) {
    return request(`/vehicles/${id}/commands/sentry`, { method: "POST", body: { on } });
  },

  setLock(id, lock) {
    return request(`/vehicles/${id}/commands/lock`, { method: "POST", body: { lock } });
  },

  // Climate preconditioning quick-action (auto_conditioning_start/stop).
  setClimate(id, on) {
    return request(`/vehicles/${id}/commands/climate`, { method: "POST", body: { on } });
  },
  // --- Whereabouts trail (slice 3a/3b) ---
  trail(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return request(`/vehicles/${id}/trail${suffix}`);
  },
  // Period metrics for the Driving tab (mileage, miles driven, trips, energy est).
  drivingSummary(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return request(`/vehicles/${id}/driving/summary${suffix}`);
  },
  // Enriched per-trip rows (Trips view) and per-driver rollup (Drivers tab).
  trips(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return request(`/vehicles/${id}/trips${suffix}`);
  },
  driversSummary(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return request(`/vehicles/${id}/trips/drivers-summary${suffix}`);
  },
  // Charge sessions + summary for a period (Charging tab).
  charging(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return request(`/vehicles/${id}/charging${suffix}`);
  },
  chargingSync(id, body) {
    return request(`/vehicles/${id}/charging/sync`, { method: "POST", body });
  },
  chargingTariff(id) {
    return request(`/vehicles/${id}/charging/tariff`);
  },
  updateChargingTariff(id, body) {
    return request(`/vehicles/${id}/charging/tariff`, { method: "PUT", body });
  },
  async chargingInvoice(id, sessionId) {
    const token = getToken();
    const res = await fetch(`${API_BASE}/vehicles/${id}/charging/sessions/${sessionId}/invoice`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 401) {
      setToken(null);
      throw new ApiError(401, "Your session expired. Sign in again.");
    }
    if (!res.ok) {
      let message = `Invoice download failed (${res.status}).`;
      try { const data = await res.json(); if (typeof data?.detail === "string") message = data.detail; } catch { /* use status */ }
      throw new ApiError(res.status, message);
    }
    return res.blob();
  },
  fsdHistory(id, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    return request(`/vehicles/${id}/driving/fsd${qs.size ? `?${qs}` : ""}`);
  },

  // --- Community charging DB (slice 4b) ---
  communityChargers(lat, lng, radius_km = 25, source = "all") {
    const qs = new URLSearchParams({ lat, lng, radius_km, source });
    return request(`/community/chargers?${qs}`);
  },
  matchCharger(lat, lng) {
    const qs = new URLSearchParams({ lat, lng });
    return request(`/community/match?${qs}`);
  },
  promoteNetworkCharger(networkId) {
    return request(`/community/chargers/from-network/${networkId}`, { method: "POST" });
  },
  createCharger(body) {
    return request("/community/chargers", { method: "POST", body });
  },
  charger(id) {
    return request(`/community/chargers/${id}`);
  },
  saveChargerReview(id, body) {
    return request(`/community/chargers/${id}/review`, { method: "PUT", body });
  },
  deleteChargerReview(id) {
    return request(`/community/chargers/${id}/review`, { method: "DELETE" });
  },
  reportCharger(id) {
    return request(`/community/chargers/${id}/report`, { method: "POST" });
  },
  reportReview(id) {
    return request(`/community/reviews/${id}/report`, { method: "POST" });
  },
  moderationQueue() {
    return request("/community/moderation");
  },
  moderateCharger(id, action) {
    return request(`/community/chargers/${id}/moderate`, { method: "POST", body: { action } });
  },
  moderateReview(id, action) {
    return request(`/community/reviews/${id}/moderate`, { method: "POST", body: { action } });
  },
  vehicleDrivers(id) {
    return request(`/vehicles/${id}/drivers`);
  },
  tagTrip(id, body) {
    return request(`/vehicles/${id}/trips/tag`, { method: "PUT", body });
  },

  // --- Account + driver profiles (settings) ---
  batteryForecast(id) {
    return request(`/vehicles/${id}/battery/forecast`);
  },
  // --- Reports ---
  reportsCatalog(id) {
    return request(`/vehicles/${id}/reports/catalog`);
  },
  reportsData(id, { metrics, start, end }) {
    const p = new URLSearchParams();
    p.set("metrics", (metrics || []).join(","));
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    return request(`/vehicles/${id}/reports/data?${p.toString()}`);
  },
  savedReports(id) {
    return request(`/vehicles/${id}/reports/saved`);
  },
  createSavedReport(id, body) {
    return request(`/vehicles/${id}/reports/saved`, { method: "POST", body });
  },
  deleteSavedReport(id, rid) {
    return request(`/vehicles/${id}/reports/saved/${rid}`, { method: "DELETE" });
  },
  setDefaultReport(id, rid) {
    return request(`/vehicles/${id}/reports/saved/${rid}/default`, { method: "POST" });
  },
  updateSavedReport(id, rid, body) {
    return request(`/vehicles/${id}/reports/saved/${rid}`, { method: "PUT", body });
  },
    account() {
    return request("/auth/me");
  },
  updateAccount(body) {
    return request("/auth/account", { method: "PATCH", body });
  },
  // Persist one surface's drag-drop layout (e.g. surface:"landing").
  saveLayout(surface, data) {
    return request("/auth/account/layout", { method: "PUT", body: { surface, data } });
  },
  changePassword(body) {
    return request("/auth/account/password", { method: "POST", body });
  },
  driverProfiles() {
    return request("/drivers");
  },
  createDriverProfile(body) {
    return request("/drivers", { method: "POST", body });
  },
  updateDriverProfile(id, body) {
    return request(`/drivers/${id}`, { method: "PATCH", body });
  },
  deleteDriverProfile(id) {
    return request(`/drivers/${id}`, { method: "DELETE" });
  },
  async uploadDriverPhoto(id, file) {
    const fd = new FormData();
    fd.append("file", file);
    const t = getToken();
    const res = await fetch(`${API_BASE}/drivers/${id}/photo`, {
      method: "POST",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
    });
    if (!res.ok) {
      let msg = `Upload failed (${res.status}).`;
      try {
        const d = await res.json();
        if (d?.detail) msg = d.detail;
      } catch (e) {
        /* ignore */
      }
      throw new ApiError(res.status, msg);
    }
    return res.json();
  },
  // Fetch the photo with auth and return an object URL (per-user secure — the
  // photo isn't publicly served). Caller should revoke the URL when done.
  async driverPhotoObjectUrl(id) {
    const t = getToken();
    const res = await fetch(`${API_BASE}/drivers/${id}/photo`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  },
  deleteDriverPhoto(id) {
    return request(`/drivers/${id}/photo`, { method: "DELETE" });
  },

  // --- IoT connectors (smart-home hubs/accounts) ---
  iotProviders() {
    return request("/iot/providers");
  },
  connections() {
    return request("/iot/connections");
  },
  createConnection(body) {
    return request("/iot/connections", { method: "POST", body });
  },
  testConnection(cid) {
    return request(`/iot/connections/${cid}/test`, { method: "POST" });
  },
  connectionDevices(cid) {
    return request(`/iot/connections/${cid}/devices`);
  },
  runDevice(cid, body) {
    return request(`/iot/connections/${cid}/run`, { method: "POST", body });
  },
  deleteConnection(cid) {
    return request(`/iot/connections/${cid}`, { method: "DELETE" });
  },

  // --- Dashcam bridge and home archive ---
  dashcamDevices() {
    return request("/dashcam/devices");
  },
  dashcamClaim(body) {
    return request("/dashcam/devices/claim", { method: "POST", body });
  },
  dashcamEnroll(body) {
    return request("/dashcam/devices/enroll", { method: "POST", body });
  },
  dashcamRevoke(id) {
    return request(`/dashcam/devices/${id}/revoke`, { method: "POST" });
  },
  dashcamSettings(id) {
    return request(`/dashcam/devices/${id}/settings`);
  },
  dashcamSaveSettings(id, body) {
    return request(`/dashcam/devices/${id}/settings`, { method: "PUT", body });
  },
  dashcamEvents(params = "") {
    return request(`/dashcam/events${params}`);
  },
  dashcamEvent(id) {
    return request(`/dashcam/events/${id}`);
  },
  dashcamStar(id) {
    return request(`/dashcam/events/${id}/star`, { method: "POST" });
  },
  dashcamPull(clipId) {
    return request(`/dashcam/clips/${clipId}/pull`, { method: "POST" });
  },
  // Media is auth-gated, so it cannot be handed to <video src> directly -- fetch
  // the bytes with the bearer token and let the caller make an object URL.
  async dashcamMedia(eventId, kind) {
    const headers = {};
    const t = getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
    const res = await fetch(`${API_BASE}/dashcam/media/${eventId}/${kind}`, { headers });
    if (!res.ok) throw new ApiError(res.status, `Media ${res.status}`);
    return res.blob();
  },
  async dashcamOriginal(clipId) {
    const headers = {};
    const t = getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
    const res = await fetch(`${API_BASE}/dashcam/clips/${clipId}/media`, { headers });
    if (!res.ok) throw new ApiError(res.status, `Original clip ${res.status}`);
    return res.blob();
  },

  // --- Passkeys (WebAuthn). A passkey is a login method, not a second factor:
  // the server requires user verification, so an assertion already carries both
  // factors and the TOTP step is skipped. ---
  passkeyList() {
    return request("/auth/passkey/credentials");
  },
  passkeyRegisterOptions() {
    return request("/auth/passkey/register/options", { method: "POST" });
  },
  passkeyRegisterVerify(body) {
    return request("/auth/passkey/register/verify", { method: "POST", body });
  },
  passkeyRename(id, label) {
    return request(`/auth/passkey/credentials/${id}`, { method: "PATCH", body: { label } });
  },
  passkeyDelete(id) {
    return request(`/auth/passkey/credentials/${id}`, { method: "DELETE" });
  },
  passkeyLoginOptions() {
    return request("/auth/passkey/login/options", { method: "POST", auth: false });
  },
  passkeyLoginVerify(body) {
    return request("/auth/passkey/login/verify", { method: "POST", body, auth: false });
  },
  recoveryStatus() {
    return request("/auth/recovery/status");
  },
  recoveryGenerate() {
    return request("/auth/recovery/generate", { method: "POST" });
  },

  // --- Geofences (Foundation) ---
  geofences() {
    return request("/geofences");
  },
  createGeofence(body) {
    return request("/geofences", { method: "POST", body });
  },
  updateGeofence(id, body) {
    return request(`/geofences/${id}`, { method: "PATCH", body });
  },
  deleteGeofence(id) {
    return request(`/geofences/${id}`, { method: "DELETE" });
  },
  geoSearch(q, bias) {
    const qs = new URLSearchParams({ q });
    if (bias && bias.lat != null && bias.lng != null) {
      qs.set("lat", bias.lat);
      qs.set("lng", bias.lng);
    }
    return request(`/geo/search?${qs}`);
  },
  mapsConfig() {
    return request("/geo/maps-config");
  },
  async uploadGeofencePhoto(id, file) {
    const fd = new FormData();
    fd.append("file", file);
    const t = getToken();
    const res = await fetch(`${API_BASE}/geofences/${id}/photo`, {
      method: "POST",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
    });
    if (!res.ok) {
      let msg = `Upload failed (${res.status}).`;
      try {
        const d = await res.json();
        if (d?.detail) msg = d.detail;
      } catch (e) {
        /* ignore */
      }
      throw new ApiError(res.status, msg);
    }
    return res.json();
  },
  async geofencePhotoObjectUrl(id) {
    const t = getToken();
    const res = await fetch(`${API_BASE}/geofences/${id}/photo`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  },
  deleteGeofencePhoto(id) {
    return request(`/geofences/${id}/photo`, { method: "DELETE" });
  },
};

export { ApiError };
